import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import Modal from '@roo/roo/Modal';
import Input from '@roo/roo/Input';
import request from '@utils/axiosWrapper';
import { successToast, failToast } from '@utils/toast';
import modalStyles from './RedPacketModal.module.scss';

const validate = (money: number, maximumCompensationAmount: number): [boolean, string] => {
    if (money === 0) {
        return [false, '输入数值需要大于 0'];
    }
    if (money > maximumCompensationAmount) {
        return [false, `单次转账金额上限为 ${maximumCompensationAmount} 元`];
    }
    return [true, ''];
};

type RedPacketButtonProps = {
    wmPoiId: string;
    wmOrderViewId: string;
    maximumCompensationAmount: number;
    settleAmount: string;
};

const RedPacketModal: React.FC<{
    visible: boolean;
    onCancel: () => void;
} & RedPacketButtonProps> = ({
    wmOrderViewId,
    wmPoiId,
    visible,
    onCancel,
    settleAmount,
    maximumCompensationAmount,
}) => {
    const [amount, setAmount] = useState<string>('');
    const [validateMessage, setValidateMessage] = useState('');
    const [edited, setEdited] = useState(false);
    useEffect(() => {
        if (edited) {
            setValidateMessage(validate(Number(amount), maximumCompensationAmount)[1]);
        }
    }, [amount, maximumCompensationAmount, edited]);

    const submitting = useRef(false);

    return visible ? (
        <Modal
            visible
            className={modalStyles.redPacketModal}
            onCancel={onCancel}
            title="小额打款"
            cancelText="取消"
            confirmText={`打款¥${Number(amount).toFixed(2)}`}
            onConfirm={() => {
                if (submitting.current) return;
                submitting.current = true;

                if (validateMessage) {
                    submitting.current = false;
                    return;
                }

                request
                    .rawPostAsFormSubmit<{ code: number; message: string }>(
                        '/gw/api/order/afterSale/recover/transferAmount',
                        {
                            wmPoiId,
                            wmOrderViewId,
                            money: Number(amount),
                            channelType: 1,
                        },
                    )
                    .then(res => {
                        if (res.code === 0) {
                            successToast('已成功小额转账');
                            onCancel();
                        } else {
                            failToast(res.message);
                        }
                        submitting.current = false;
                    })
                    .catch(err => {
                        failToast(err.message);
                        submitting.current = false;
                    });
            }}
            confirmButtonProps={{ disabled: Boolean(validateMessage) }}
        >
            <div className={modalStyles.header}>
                <img className={modalStyles.guideIcon} src={require('./assets/guide.png')} />
                <div className={modalStyles.content}>
                    操作前请您务必在电话或聊天中与顾客协商并征得其同意。未经顾客同意直接操作极大概率会带来顾客投诉。
                </div>
            </div>
            <div className={modalStyles.body}>
                <div>请输入打款金额</div>
                <div className={modalStyles.inputWrapper}>
                    <Input
                        prefix="¥"
                        value={amount}
                        onChange={e => {
                            // 不允许整数部分多余的 0
                            const value = e.target.value.replace(
                                /^0+(\d+(\.\d+)?)?/i,
                                (_, $1) => $1 || '0',
                            );

                            // 整数部分最多三位，小数部分最多两位
                            if (!value || /^\d{1,3}(\.\d{0,2})?$/i.test(value)) {
                                setAmount(value);
                                setEdited(true);
                            }
                        }}
                        className={classNames({
                            [modalStyles.error]: validateMessage,
                        })}
                    />
                    {settleAmount ? (
                        <div className={modalStyles.settleAmount}>{settleAmount}</div>
                    ) : null}
                </div>
                {validateMessage ? (
                    <div className={modalStyles.validateMessage}>{validateMessage}</div>
                ) : null}
            </div>
            <div className={modalStyles.tip}>
                * 打款金额将直接打入顾客美团钱包，操作后无法退回。
            </div>
        </Modal>
    ) : null;
};

export default RedPacketModal;
